import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Computer as ServerIcon,
  Star as CrownIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Hub as HubIcon,
  HowToVote as ElectionIcon,
} from '@mui/icons-material';
import AdminPageWrapper from '../components/AdminPageWrapper';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorAlert from '../components/ErrorAlert';
import { clusterAPI } from '../services/api';

const AdminClusterPage = () => {
  const [clusterStatus, setClusterStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    fetchClusterStatus();
    // Refresh every 10 seconds
    const interval = setInterval(fetchClusterStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchClusterStatus = async () => {
    try {
      setError(null);
      const data = await clusterAPI.getStatus();
      setClusterStatus(data);
    } catch (error) {
      console.error('Failed to fetch cluster status:', error);
      setError('Failed to fetch cluster status');
    } finally {
      setLoading(false);
    }
  };

  const forceElection = async () => {
    setActionLoading('force_election');
    setError(null);
    setSuccessMessage(null);
    try {
      const data = await clusterAPI.forceElection();

      if (data.success) {
        setSuccessMessage(data.message);
        fetchClusterStatus(); // Refresh status
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error('Failed to force election:', error);
      setError('Failed to force master election');
    } finally {
      setActionLoading(null);
    }
  };

  const cleanupStaleNodes = async () => {
    setActionLoading('cleanup_nodes');
    setError(null);
    setSuccessMessage(null);
    try {
      const data = await clusterAPI.cleanupNodes();

      if (data.success) {
        setSuccessMessage(data.message);
        fetchClusterStatus(); // Refresh status
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error('Failed to cleanup stale nodes:', error);
      setError('Failed to cleanup stale nodes');
    } finally {
      setActionLoading(null);
    }
  };

  // Determine if a node is healthy based on last heartbeat
  const getNodeHealth = (lastHeartbeat) => {
    if (!lastHeartbeat) return 'unknown';

    const lastHeartbeatTime = new Date(lastHeartbeat);
    const now = new Date();
    const diffMinutes = (now - lastHeartbeatTime) / (1000 * 60);

    if (diffMinutes < 2) return 'healthy';  // Less than 2 minutes - healthy
    if (diffMinutes < 10) return 'warning'; // 2-10 minutes - warning
    return 'unhealthy'; // More than 10 minutes - unhealthy/stale
  };


  const getStatusChip = (status, lastHeartbeat = null) => {
    // For nodes, calculate health based on heartbeat
    const effectiveStatus = lastHeartbeat ? getNodeHealth(lastHeartbeat) : status;

    switch (effectiveStatus) {
      case 'healthy':
        return <Chip icon={<CheckCircleIcon />} label="Healthy" color="success" size="small" />;
      case 'warning':
        return <Chip icon={<ErrorIcon />} label="Stale" color="warning" size="small" />;
      case 'unhealthy':
        return <Chip icon={<ErrorIcon />} label="Unhealthy" color="error" size="small" />;
      case 'unknown':
        return <Chip label="Unknown" variant="outlined" size="small" />;
      case 'running':
        return <Chip icon={<PlayIcon />} label="Running" color="info" size="small" />;
      case 'enabled':
        return <Chip icon={<CheckCircleIcon />} label="Enabled" color="success" size="small" />;
      case 'disabled':
        return <Chip label="Disabled" variant="outlined" size="small" />;
      case 'error':
        return <Chip icon={<ErrorIcon />} label="Error" color="error" size="small" />;
      default:
        return <Chip label={effectiveStatus} variant="outlined" size="small" />;
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  if (loading) {
    return (
      <AdminPageWrapper>
        <Box display="flex" justifyContent="center" p={4}>
          <LoadingSpinner />
        </Box>
      </AdminPageWrapper>
    );
  }

  if (!clusterStatus) {
    return (
      <AdminPageWrapper>
        <Alert severity="error" sx={{ m: 2 }}>
          Failed to load cluster status
        </Alert>
      </AdminPageWrapper>
    );
  }

  return (
    <AdminPageWrapper>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <HubIcon fontSize="large" />
              Cluster Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitor and manage the AgentFlow cluster
            </Typography>
          </Box>
          <Button 
            onClick={fetchClusterStatus} 
            variant="outlined"
            disabled={loading}
            startIcon={<RefreshIcon />}
          >
            Refresh
          </Button>
        </Box>

        {/* Error and Success Messages */}
        {error && (
          <ErrorAlert 
            message={error} 
            onClose={() => setError(null)}
            sx={{ mb: 3 }}
          />
        )}

        {successMessage && (
          <Alert 
            severity="success" 
            onClose={() => setSuccessMessage(null)}
            sx={{ mb: 3 }}
          >
            {successMessage}
          </Alert>
        )}

        {/* Cluster Overview */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Total Nodes
                    </Typography>
                    <Typography variant="h4">
                      {clusterStatus.cluster_info?.total_nodes || 0}
                    </Typography>
                  </Box>
                  <ServerIcon color="action" fontSize="large" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Healthy Nodes
                    </Typography>
                    <Typography variant="h4">
                      {clusterStatus.nodes?.filter(node => getNodeHealth(node.last_heartbeat) === 'healthy').length || 0}
                    </Typography>
                  </Box>
                  <CheckCircleIcon color="success" fontSize="large" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Current Master
                    </Typography>
                    <Typography variant="body1">
                      {clusterStatus.cluster_info?.current_master || 'None'}
                    </Typography>
                  </Box>
                  <CrownIcon sx={{ color: 'warning.main' }} fontSize="large" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      This Node
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">
                        {clusterStatus.cluster_info?.this_node_id}
                      </Typography>
                      {clusterStatus.cluster_info?.is_master && (
                        <CrownIcon sx={{ color: 'warning.main' }} fontSize="small" />
                      )}
                    </Box>
                  </Box>
                  <ServerIcon color="action" fontSize="large" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Master Election */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CrownIcon />
              Master Election
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Manage cluster leadership and master node election
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="body2">
                  Current master: <strong>{clusterStatus.cluster_info?.current_master || 'None'}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This node is {clusterStatus.cluster_info?.is_master ? 'master' : 'not master'}
                </Typography>
              </Box>
              <Tooltip title="Force a new master election with PRM node priority. Expires the current master's lease and allows all nodes to compete for master role.">
                <Button
                  onClick={forceElection}
                  variant="outlined"
                  disabled={actionLoading === 'force_election'}
                  startIcon={actionLoading === 'force_election' ? <CircularProgress size={20} /> : <ElectionIcon />}
                >
                  Force Re-Election
                </Button>
              </Tooltip>
            </Box>
          </CardContent>
        </Card>

        {/* Cluster Nodes */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ServerIcon />
                  Cluster Nodes
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  All nodes in the cluster and their current status
                </Typography>
              </Box>
              <Button
                onClick={cleanupStaleNodes}
                variant="outlined"
                size="small"
                disabled={actionLoading === 'cleanup_nodes'}
                startIcon={actionLoading === 'cleanup_nodes' ? <CircularProgress size={16} /> : <RefreshIcon />}
              >
                Cleanup Stale
              </Button>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Node ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Site</TableCell>
                    <TableCell>Host</TableCell>
                    <TableCell>Last Heartbeat</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clusterStatus.nodes?.map((node) => (
                    <TableRow key={node.node_id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {node.node_id}
                          {node.is_master && <CrownIcon sx={{ color: 'warning.main' }} fontSize="small" />}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {getStatusChip(node.status, node.last_heartbeat)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {node.site_type || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {node.hostname}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {node.ip_address}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatTimestamp(node.last_heartbeat)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </AdminPageWrapper>
  );
};

export default AdminClusterPage;